'use client';

import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  scrollable?: boolean;
  size?: ModalSize;
  /** Hide the default title bar (useful when children render their own chrome). */
  hideHeader?: boolean;
  /** `right` docks the panel to the screen edge, matching the asset side drawers. */
  placement?: 'center' | 'right';
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full',
};

const Modal: React.FC<ModalProps> = React.memo(function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  scrollable = true,
  size = 'md',
  hideHeader = false,
  placement = 'center',
}) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  const isFullscreen = placement === 'center' && size === 'full';
  const isRight = placement === 'right';

  return createPortal(
    <div className={`${isFullscreen || isRight ? 'fixed inset-0 z-[1000] flex overflow-hidden' : 'fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto p-3 sm:items-center sm:p-4'} ${isRight ? 'justify-end' : ''}`}>
      {/* Backdrop */}
      <div
        className={`${isFullscreen || isRight ? 'fixed inset-0' : 'absolute inset-0'} ${isRight ? 'bg-slate-900/50 backdrop-blur-xs' : 'bg-black/40 backdrop-blur-sm'} hrm-fade-in`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div
        className={`
          ${
            isRight
              ? 'relative z-10 flex h-full w-full max-w-[min(100vw,1440px)] flex-col border-l border-slate-200 bg-white shadow-2xl animate-in slide-in-from-right duration-300 dark:border-slate-800 dark:bg-slate-950'
              : isFullscreen
                ? 'fixed inset-0 flex flex-col rounded-none bg-white dark:bg-slate-900 shadow-xl shadow-slate-900/10 hrm-modal-enter'
                : `relative my-auto flex w-full ${sizeClasses[size]} max-h-[calc(100dvh-1.5rem)] flex-col rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 dark:border-slate-800 dark:bg-slate-900 sm:max-h-[90vh] hrm-modal-enter`
          }
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        {!hideHeader && (
        <div className={`flex items-start justify-between ${isFullscreen ? 'px-8 py-6' : 'px-6 pt-6 pb-4'} border-b border-slate-100 dark:border-slate-800`}>
          <div className="min-w-0 flex-1">
            <h2
              id="modal-title"
              className={`font-bold text-slate-900 dark:text-white truncate ${isFullscreen ? 'text-2xl' : 'text-lg'}`}
            >
              {title}
            </h2>
            {subtitle && (
              <p className={`mt-1 text-slate-500 dark:text-slate-400 ${isFullscreen ? 'text-base' : 'text-sm'}`}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="
              ml-4 flex-shrink-0 p-1.5 rounded-xl
              text-slate-400 hover:text-slate-600
              dark:text-slate-500 dark:hover:text-slate-300
              hover:bg-slate-100 dark:hover:bg-slate-800
              transition-colors
            "
            aria-label="Close modal"
          >
            <i className={`ri-close-line ${isFullscreen ? 'text-2xl' : 'text-xl'}`} />
          </button>
        </div>
        )}
        {hideHeader && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close modal"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        )}

        {/* Scrollable body */}
        <div
          className={`flex-1 min-h-0 ${
            isRight
              ? 'overflow-hidden px-4 py-4'
              : scrollable
                ? 'overflow-y-auto'
                : isFullscreen
                  ? 'overflow-hidden'
                  : 'overflow-visible'
          } ${!isRight && (isFullscreen ? (scrollable ? 'px-8 py-6' : 'px-0 py-0') : 'px-6 py-4')}`}
        >
          {children}
        </div>

        {/* Optional footer */}
        {footer && (
          <div className={`border-t border-slate-100 dark:border-slate-800 ${isFullscreen ? 'px-8 py-6' : 'px-6 py-4'}`}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
});

export default Modal;
